<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div class="d-flex align-items-center">
          <button @click="back" class="btn btn-outline-secondary btn-icon-text py-2 px-3 mr-3">
            <i class="mdi mdi-arrow-left mr-1"></i> {{ $t('common.back') }}
          </button>
          <div>
            <h2 class="text-white font-weight-bold mb-1">{{ $t('quests.createConsequence') }}</h2>
            <p class="text-muted small mb-0">{{ $t('quests.consequencesSubtitle') }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 grid-margin">
      <div class="card border border-secondary">
        <div class="card-body">
          <!-- Quick Presets -->
          <div class="rounded mb-4 p-3 d-flex align-items-center justify-content-between flex-wrap gap-2" style="background: rgba(30, 41, 59, 0.4); border: 1px solid #27272a;">
            <div class="d-flex align-items-center mb-2 mb-md-0">
              <i class="mdi mdi-lightning-bolt text-warning font-weight-bold mr-2" style="font-size: 1.25rem;"></i>
              <div>
                <div class="text-white font-weight-bold small">{{ $currentLocale === 'id' ? 'Templat Sanksi Cepat' : 'Quick Consequence Presets' }}</div>
                <div class="text-muted small">{{ $currentLocale === 'id' ? 'Klik untuk mengisi formulir aturan konsekuensi secara otomatis' : 'Click a preset to auto-fill the SOP consequence form' }}</div>
              </div>
            </div>
            <div class="d-flex flex-wrap gap-2">
              <button type="button" @click="applyPreset('late')" class="btn btn-xs btn-outline-warning mr-1 mb-1">
                <i class="mdi mdi-clock-alert-outline mr-1"></i> {{ $currentLocale === 'id' ? 'Terlambat Hadir (-10 Gold)' : 'Late Attendance (-10 Gold)' }}
              </button>
              <button type="button" @click="applyPreset('deadline')" class="btn btn-xs btn-outline-danger mr-1 mb-1">
                <i class="mdi mdi-alert-circle-outline mr-1"></i> {{ $currentLocale === 'id' ? 'Terlewat Tenggat (-25 Gold)' : 'Missed Deadline (-25 Gold)' }}
              </button>
              <button type="button" @click="applyPreset('sop')" class="btn btn-xs btn-outline-danger mr-1 mb-1">
                <i class="mdi mdi-shield-alert-outline mr-1"></i> {{ $currentLocale === 'id' ? 'Pelanggaran SOP (-50 Gold)' : 'SOP Breach (-50 Gold)' }}
              </button>
              <button type="button" @click="applyPreset('social')" class="btn btn-xs btn-outline-info mb-1">
                <i class="mdi mdi-account-group-outline mr-1"></i> {{ $currentLocale === 'id' ? 'Tugas Sosial (Non-Gold)' : 'Social Task (Non-Gold)' }}
              </button>
            </div>
          </div>

          <form class="forms-sample" method="post" @submit.prevent="submitForm">
            <div class="form-group row">
              <div class="col-md-6 mb-3">
                <label for="nameInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('rulesForm.consequenceName') }}</label>
                <input
                  id="nameInput"
                  type="text"
                  class="form-control"
                  v-model="ruleStore.name"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: Terlambat Menghadiri Rapat Harian' : 'e.g. Late for Daily Standup Meeting'"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label for="punishmentInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('rulesForm.penaltyType') }}</label>
                <select class="form-control" id="punishmentInput" v-model="ruleStore.punishment">
                  <option value="gold">{{ $t('rulesForm.penaltyGold') }} (-Gold)</option>
                  <option value="non_gold">{{ $t('rulesForm.penaltyNonGold') }}</option>
                </select>
              </div>
            </div>

            <div class="form-group row">
              <div class="col-md-6 mb-3" v-if="ruleStore.punishment === 'gold'">
                <label for="goldInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('quests.pointsDeduction') }}</label>
                <div class="input-group">
                  <input
                    id="goldInput"
                    type="number"
                    class="form-control"
                    v-model="ruleStore.gold"
                    min="1"
                    placeholder="10"
                    required
                  />
                  <div class="input-group-append">
                    <span class="input-group-text bg-dark border-secondary text-danger font-weight-bold">
                      <i class="mdi mdi-minus-circle mr-1"></i> Gold
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-md-6 mb-3" v-else>
                <label for="nonGoldInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('rulesForm.penaltyNonGold') }}</label>
                <textarea
                  class="form-control"
                  id="nonGoldInput"
                  rows="2"
                  v-model="ruleStore.non_gold"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: Berikan sesi berbagi santai 10 menit' : 'e.g. Deliver a 10-minute best practice sharing session'"
                  required
                ></textarea>
              </div>
              <div class="col-md-6 mb-3">
                <label for="descriptionInput" class="text-muted small text-uppercase font-weight-bold">{{ $t('common.description') }}</label>
                <textarea
                  class="form-control"
                  id="descriptionInput"
                  rows="2"
                  v-model="ruleStore.description"
                  :placeholder="$currentLocale === 'id' ? 'Jelaskan kriteria pelanggaran SOP ini...' : 'Describe the criteria for this SOP violation...'"
                ></textarea>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2">
              <button type="button" @click="back" class="btn btn-outline-secondary px-4 py-2 mr-2" :disabled="loading">
                {{ $t('common.back') }}
              </button>
              <button type="submit" class="btn btn-primary px-4 py-2 font-weight-bold" :disabled="loading">
                <span class="spinner-border spinner-border-sm mr-1" v-if="loading" role="status"></span>
                <i class="mdi mdi-check-all mr-1" v-else></i>
                {{ $t('common.submit') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { storeConsequence } from '../../services/consequenceService';
import Swal from 'sweetalert2';

export default {
  name: 'CreateConsequence',
  data: () => ({
    ruleStore: {
      name: '',
      gold: null,
      punishment: 'gold',
      non_gold: null,
      description: '',
    },
    loading: false,
  }),
  watch: {
    'ruleStore.punishment': function (newVal) {
      if (newVal === 'gold') {
        this.ruleStore.non_gold = null;
      } else {
        this.ruleStore.gold = null;
      }
    },
  },
  methods: {
    back() {
      this.$router.go(-1);
    },
    applyPreset(type) {
      const presets = {
        late: {
          name: 'Terlambat Hadir Standup Meeting / Daily Check-in',
          punishment: 'gold',
          gold: 10,
          non_gold: null,
          description: 'Terlambat lebih dari 10 menit tanpa pemberitahuan sebelumnya di kanal koordinasi tim.',
        },
        deadline: {
          name: 'Terlewat Deadline Sprint / Milestone Tanpa Konfirmasi',
          punishment: 'gold',
          gold: 25,
          non_gold: null,
          description: 'Tugas atau milestone tertunda melewati batas waktu tanpa eskalasi kendala atau re-negosiasi.',
        },
        sop: {
          name: 'Pelanggaran SOP / Melewatkan Quality Check',
          punishment: 'gold',
          gold: 50,
          non_gold: null,
          description: 'Tidak mengikuti alur review, merge ke branch produksi tanpa approval, atau bypass SOP yang disepakati.',
        },
        social: {
          name: 'Tugas Sosial / Sesi Sharing Santai Tim',
          punishment: 'non_gold',
          gold: null,
          non_gold: 'Membawakan presentasi ringan atau sharing materi bermanfaat selama 15 menit pada pertemuan tim berikutnya.',
          description: 'Bentuk sanksi edukatif untuk mempererat tim dan menyebarkan wawasan baru bagi seluruh anggota tim.',
        },
      };

      const preset = presets[type];
      if (preset) {
        this.ruleStore.name = preset.name;
        this.ruleStore.punishment = preset.punishment;
        this.ruleStore.gold = preset.gold;
        this.ruleStore.non_gold = preset.non_gold;
        this.ruleStore.description = preset.description;
      }
    },
    async submitForm() {
      this.loading = true;
      try {
        await storeConsequence(this.ruleStore);
        Swal.fire({
          title: this.$t('common.success'),
          text: this.$t('rulesForm.savedSuccess'),
          icon: 'success',
        });
        this.$router.push('/rules/consequences');
      } catch (error) {
        console.error('Error creating consequence:', error);
        Swal.fire({
          title: this.$t('common.error'),
          text: this.$currentLocale === 'id' ? 'Gagal menyimpan aturan konsekuensi.' : 'Failed to create consequence rule.',
          icon: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
